/**
 * Which platform blocks this theme restyles, and how wide each one sits.
 *
 * <p><strong>This data lives outside `withTradeSkin.tsx` on purpose, and the
 * reason is a build failure rather than tidiness.</strong> That file carries
 * `'use client'`, and every export of a client module reaches a server module
 * as a *reference* rather than as the value itself. The theme bundle in
 * `../index.ts` is evaluated wherever the theme is imported — including from
 * the server, through `app/api/storefront/extensions/[...path]/route.ts`,
 * which pulls the extensions module in to read its API prefixes. Reading this
 * map there has to see real data, so the map cannot be declared behind the
 * client boundary.
 *
 * <p>Keeping it here also keeps it testable from anywhere: the theme's own test
 * asserts every block the template's required pages use appears in this map,
 * and that test is not a React environment.
 */

/** How wide the functional content sits. Forms read best narrow; baskets and grids need room. */
export type SkinWidth = 'narrow' | 'medium' | 'wide';

/**
 * The platform blocks this theme restyles, with the width each sits at.
 *
 * <p>Every page the storefront requires uses one of these for its functional
 * part. Without an entry a block renders as an unstyled platform default, which
 * is what the whole site did before this file existed: the brand survived as far
 * as the header and then vanished the moment a visitor started buying.
 */
export const SKINNED_BLOCKS: Record<string, SkinWidth> = {
  // Buying
  ProductGrid: 'wide',
  FacetPanel: 'wide',
  ProductDetail: 'wide',
  CartView: 'medium',
  CheckoutForm: 'medium',
  OrderConfirmation: 'medium',
  OrderTracking: 'narrow',

  // Account
  AccountDashboard: 'medium',
  AccountOrderDetail: 'medium',
  AddressBook: 'medium',
  SavedListsView: 'medium',

  // Getting in
  SignInForm: 'narrow',
  RegisterForm: 'narrow',
  ForgotPasswordForm: 'narrow',
  ResetPasswordForm: 'narrow',
  ActivateAccountForm: 'narrow',
  ConfirmEmailChange: 'narrow',

  // Trade — the second engine (BUSINESS_PLAN §0.6.2) — is NOT skinned, it is REPLACED.
  // `TradeApplicationForm` used to sit here at 'medium'. The skin can only reach a block
  // from the outside, and the shared trade form styles every field with inline style
  // objects (`account/accountShared.tsx`), which no stylesheet can override; the approved
  // mockup's numbered panels were therefore unreachable. The theme now registers its own
  // block under that same type (`SolarTradeApplication`, see `../index.ts`), which runs the
  // shared `useTradeApplication` flow under its own layout. An entry here would wrap that
  // block in a skin that re-enters the resolver and finds the same block again, so there
  // must NOT be one — and the block draws its own `.ae` scope and stylesheets in its place.

  // Content
  RichText: 'medium',
  FaqAccordion: 'medium',
  DynamicFormBlock: 'narrow',
};
